import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Container,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  IconButton,
  Chip,
  Divider,
  Collapse,
  TableSortLabel,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  MedicalServices as MedicalIcon,
  Clear as ClearIcon,
  AttachFile as AttachFileIcon,
  Restore as RestoreIcon,
  Close as CloseIcon,
  Add as AddIcon,
  FilterList as FilterIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Pets as PetsIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import DateField from './DateField';
import HelpButton from './HelpButton';
import { formatDate } from '../utils/dateUtils';
import { translateCostClass } from '../utils/translationUtils';
import CostClassHistoryPanel from './CostClassHistoryPanel';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

interface Freed {
  id: number;
  chip_id?: string;
  name?: string;
  sex?: string;
  age?: string;
  breed?: string;
  color?: string;
  arrival_date?: string;
  departure_date?: string;
  municipality?: string;
  source?: string;
  address_where_found?: string;
  initial_health_status?: string;
  fiv?: string;
  felv?: string;
  cost_class?: string;
  temporary_placement?: string;
  area?: string;
  sterilized?: string;
  notes?: string;
  photo_file_path?: string;
  freed_date?: string;
  referente_di_colonia?: string;
}

interface Column {
  column_name: string;
  data_type: string;
}

interface FilterCriteria {
  id: string;
  field: string;
  value: string;
}

const FreedList: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [freed, setFreed] = useState<Freed[]>([]);
  const [filteredFreed, setFilteredFreed] = useState<Freed[]>([]);
  const [loading, setLoading] = useState(true);
  const [freedColumns, setFreedColumns] = useState<Column[]>([]);
  const [municipalities, setMunicipalities] = useState<string[]>([]);

  // Detail panel state
  const [detailFreed, setDetailFreed] = useState<Freed | null>(null);

  // Filter state - multi-field with AND logic
  const [filters, setFilters] = useState<FilterCriteria[]>([]);
  const [currentFilterField, setCurrentFilterField] = useState<string>('');
  const [currentFilterValue, setCurrentFilterValue] = useState<string>('');
  const [filtersExpanded, setFiltersExpanded] = useState(true);

  // Column widths for resizable table
  const [colWidths, setColWidths] = useState<Record<string, number>>({
    id: 50, chip_id: 130, name: 120, sex: 48,
    color: 80, municipality: 110, fiv: 52, felv: 52, freed_date: 100, referente_di_colonia: 130, cost_class: 110,
  });
  const [sortCol, setSortCol] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const handleSort = (col: string) => {
    if (sortCol === col) { setSortDir(d => d === 'asc' ? 'desc' : 'asc'); }
    else { setSortCol(col); setSortDir('asc'); }
  };
  const sortedFreed = useMemo(() => {
    if (!sortCol) return filteredFreed;
    return [...filteredFreed].sort((a, b) => {
      const ar = (a as any)[sortCol] ?? '';
      const br = (b as any)[sortCol] ?? '';
      const an = Number(ar), bn = Number(br);
      if (!isNaN(an) && !isNaN(bn) && ar !== '' && br !== '') {
        return sortDir === 'asc' ? an - bn : bn - an;
      }
      const av = String(ar).toLowerCase();
      const bv = String(br).toLowerCase();
      return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    });
  }, [filteredFreed, sortCol, sortDir]);
  const resizingRef = useRef<{ col: string; startX: number; startWidth: number } | null>(null);
  const handleResizeMouseDown = useCallback((col: string, e: React.MouseEvent) => {
    e.preventDefault(); e.stopPropagation();
    resizingRef.current = { col, startX: e.clientX, startWidth: colWidths[col] };
    const onMouseMove = (ev: MouseEvent) => {
      if (!resizingRef.current) return;
      setColWidths(prev => ({ ...prev, [resizingRef.current!.col]: Math.max(40, resizingRef.current!.startWidth + ev.clientX - resizingRef.current!.startX) }));
    };
    const onMouseUp = () => { resizingRef.current = null; document.removeEventListener('mousemove', onMouseMove); document.removeEventListener('mouseup', onMouseUp); };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [colWidths]);

  // Edit freed dialog state
  const [editFreedOpen, setEditFreedOpen] = useState(false);
  const [selectedFreed, setSelectedFreed] = useState<Freed | null>(null);
  const [freedFormData, setFreedFormData] = useState<any>({});
  const [editPhotoFile, setEditPhotoFile] = useState<File | null>(null);

  // Add event dialog state
  const [addEventOpen, setAddEventOpen] = useState(false);
  const [eventFormData, setEventFormData] = useState<any>({});
  const [eventFreed, setEventFreed] = useState<Freed | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  // Message state
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Restore dialog state
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [freedToRestore, setFreedToRestore] = useState<Freed | null>(null);
  const [reEntryDate, setReEntryDate] = useState<string>('');

  useEffect(() => {
    loadFreed();
    loadFreedColumns();
    loadMunicipalities();
  }, []);

  useEffect(() => {
    applyFilter();
  }, [freed, filters]);

  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setSelectedFile(event.target.files[0]);
    }
  };

  const handleFileUpload = async () => {
    if (!selectedFile) return null;

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await api.post(`${API_URL}/api/upload/invoice`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data.file_path;
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: `File upload failed: ${error.response?.data?.detail || error.message}`,
      });
      return null;
    }
  };

  const loadFreed = async () => {
    try {
      setLoading(true);
      // Phase 4: Query unified cats table with status=freed filter
      const response = await api.get(`${API_URL}/api/tables/public/cats/data?status=freed&limit=1000`);
      const columns: string[] = response.data.columns;
      const freedData = response.data.rows.map((row: any[]) => {
        const freed: any = {};
        columns.forEach((col, i) => {
          freed[col] = row[i];
        });
        return freed as Freed;
      });
      setFreed(freedData);
      setFilteredFreed(freedData);
    } catch (error) {
      console.error('Error loading freed cats:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadFreedColumns = async () => {
    try {
      // Phase 4: Use unified cats table columns
      const response = await api.get(`${API_URL}/api/tables/public/cats/columns`);
      setFreedColumns(response.data);
    } catch (error) {
      console.error('Error loading freed columns:', error);
    }
  };

  const loadMunicipalities = async () => {
    try {
      const response = await api.get(`${API_URL}/api/tables/public/municipalities/data?limit=1000`);
      const municipalitiesData = response.data.rows.map((row: any[]) => row[0]);
      setMunicipalities(municipalitiesData.sort());
    } catch (error) {
      console.error('Error loading municipalities:', error);
    }
  };

  const applyFilter = () => {
    if (filters.length === 0) {
      setFilteredFreed(freed);
      return;
    }
    const filtered = freed.filter((cat) =>
      filters.every((filter) => {
        const fieldValue = cat[filter.field as keyof Freed];
        if (fieldValue === null || fieldValue === undefined) return false;
        return String(fieldValue).toLowerCase().includes(filter.value.toLowerCase());
      })
    );
    setFilteredFreed(filtered);
  };

  const addFilter = () => {
    if (!currentFilterField || !currentFilterValue) return;
    setFilters([...filters, { id: Date.now().toString(), field: currentFilterField, value: currentFilterValue }]);
    setCurrentFilterField('');
    setCurrentFilterValue('');
  };

  const removeFilter = (filterId: string) => {
    setFilters(filters.filter((f) => f.id !== filterId));
  };

  const clearAllFilters = () => {
    setFilters([]);
    setCurrentFilterField('');
    setCurrentFilterValue('');
  };

  const getFieldLabel = (fieldName: string): string => {
    const labels: Record<string, string> = {
      chip_id: 'Chip ID', name: 'Name', sex: 'Sex', age: 'Age', breed: 'Breed',
      municipality: 'Municipality', source: 'Source', sterilized: 'Sterilized',
      fiv: 'FIV', felv: 'FeLV', area: 'Area', temporary_placement: 'Temp. Placement',
      freed_date: 'Freed Date', referente_di_colonia: 'Referente',
    };
    return labels[fieldName] || fieldName;
  };

  const handleRowClick = (cat: Freed) => {
    setDetailFreed(cat);
  };

  const handleEditFreed = (cat: Freed) => {
    setSelectedFreed(cat);
    setFreedFormData({
      chip_id: cat.chip_id || '',
      name: cat.name || '',
      sex: cat.sex || '',
      age: cat.age || '',
      breed: cat.breed || '',
      source: cat.source || '',
      municipality: cat.municipality || '',
      arrival_date: cat.arrival_date || '',
      departure_date: cat.departure_date || '',
      address_where_found: cat.address_where_found || '',
      temporary_placement: cat.temporary_placement || '',
      area: cat.area || '',
      notes: cat.notes || '',
      photo_file_path: cat.photo_file_path || '',
      freed_date: cat.freed_date || '',
      referente_di_colonia: cat.referente_di_colonia || '',
    });
    setEditPhotoFile(null);
    setEditFreedOpen(true);
  };

  const handleUpdateFreed = async () => {
    try {
      let updatedData = { ...freedFormData };
      if (updatedData.sterilized === 'U') updatedData.sterilized = null;

      // Upload photo if a new one is selected
      if (editPhotoFile) {
        const formData = new FormData();
        formData.append('file', editPhotoFile);

        const uploadResponse = await api.post(`/api/upload/photo`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        updatedData.photo_file_path = uploadResponse.data.file_path;
      }

      // Store cost_class before filtering in case we need it for history
      const costClassChanged = selectedFreed && updatedData.cost_class && updatedData.cost_class !== selectedFreed.cost_class;
      const newCostClass = updatedData.cost_class;

      // Filter out columns that don't exist in the freed table
      const validColumns = new Set(freedColumns.map(col => col.column_name));
      const filteredData: any = {};
      Object.entries(updatedData).forEach(([key, value]) => {
        if (validColumns.has(key)) {
          filteredData[key] = value;
        }
      });

      await api.put(`/api/records`, {
        table: 'cats',
        schema: 'public',
        record_id: selectedFreed?.id,
        data: filteredData,
      });

      // Upsert a cost-class history entry for today
      if (costClassChanged) {
        const todayStr = new Date().toISOString().split('T')[0];
        try {
          await api.post(`/api/cost-class-history/cost_class_history/cat_id/${selectedFreed.id}/upsert`, {
            cost_class: newCostClass,
            effective_from: todayStr,
          });
        } catch (e: any) { console.error('Cost class history upsert failed:', e?.response?.data || e?.message); }
      }

      setMessage({ type: 'success', text: t('checkout.freedCatUpdated') });
      setEditFreedOpen(false);
      setEditPhotoFile(null);

      // Reload freed cats and update selectedFreed with fresh data
      const response = await api.get(`${API_URL}/api/tables/public/cats/data?status=freed&limit=1000`);
      const columns: string[] = response.data.columns;
      const freedData = response.data.rows.map((row: any[]) => {
        const freed: any = {};
        columns.forEach((col, i) => {
          freed[col] = row[i];
        });
        return freed as Freed;
      });
      setFreed(freedData);
      setFilteredFreed(freedData);

      // Update selectedFreed with fresh data from the reloaded list
      const updatedCat = freedData.find((f: Freed) => f.id === selectedFreed?.id);
      if (updatedCat) {
        setSelectedFreed(updatedCat);
        setFreedFormData(updatedCat);
      }

      setTimeout(() => setMessage(null), 3000);
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to update freed cat';
      setMessage({ type: 'error', text: errorMessage });
      setTimeout(() => setMessage(null), 5000);
    }
  };

  const handleAddEventForFreed = (cat: Freed) => {
    setEventFreed(cat);
    setEventFormData({ event_date: getTodayDate() });
    setSelectedFile(null);
    setAddEventOpen(true);
  };

  const handleAddEvent = async () => {
    try {
      const eventType = eventFormData.event_type;

      // Check for duplicate Sterilization event (Phase 5: unified medical table)
      if (eventType === 'Sterilization') {
        const eventsResponse = await api.get(
          `${API_URL}/api/tables/public/medical/data?limit=1000`
        );
        const medCols: string[] = eventsResponse.data.columns;
        const catIdIdx = medCols.indexOf('cat_id');
        const eventTypeIdx = medCols.indexOf('event_type');
        const existingEvents = eventsResponse.data.rows.filter(
          (row: any[]) => row[catIdIdx] === eventFreed?.id
        );
        const hasSterilization = existingEvents.some((row: any[]) => row[eventTypeIdx] === 'Sterilization');
        if (hasSterilization) {
          setMessage({
            type: 'error',
            text: 'This cat is already sterilized. Each cat can only be sterilized once.',
          });
          return;
        }
      }

      // Upload file if selected
      let filePath = null;
      if (selectedFile) {
        filePath = await handleFileUpload();
        if (!filePath) {
          setMessage({ type: 'error', text: 'File upload failed. Please try again.' });
          return;
        }
      }

      await api.post(`${API_URL}/api/records`, {
        table: 'medical',
        schema: 'public',
        data: {
          cat_id: eventFreed?.id,
          ...eventFormData,
          ...(filePath && { invoice_file_path: filePath }),
        },
      });

      setMessage({ type: 'success', text: t('medical.eventAdded') });
      setAddEventOpen(false);
      setEventFormData({ event_date: getTodayDate() });
      setSelectedFile(null);
      loadFreed();
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to add medical event' });
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const handleRestoreClick = (freed: Freed) => {
    setFreedToRestore(freed);
    const d = new Date();
    setReEntryDate(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
    setRestoreDialogOpen(true);
  };

  const handleRestoreConfirm = async () => {
    if (!freedToRestore) return;
    if (!reEntryDate) {
      setMessage({ type: 'error', text: t('checkout.reEntryDateRequired') });
      setTimeout(() => setMessage(null), 3000);
      return;
    }

    try {
      const response = await api.post(`${API_URL}/api/restore-cat`, {
        source_table: 'freed',
        source_id: freedToRestore.id,
        re_entry_date: reEntryDate,
      });

      setMessage({ type: 'success', text: response.data.message });
      setRestoreDialogOpen(false);
      setFreedToRestore(null);
      loadFreed();
      setTimeout(() => setMessage(null), 3000);
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.detail || 'Failed to restore cat to active database'
      });
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const renderFormField = (column: Column, formData: any, setFormData: any) => {
    if (column.column_name === 'id') return null;

    if (column.column_name === 'sex') {
      return (
        <FormControl fullWidth margin="normal" key={column.column_name}>
          <InputLabel>{t('cat.sex')}</InputLabel>
          <Select
            value={formData[column.column_name] || ''}
            label={t('cat.sex')}
            onChange={(e) => setFormData({ ...formData, [column.column_name]: e.target.value })}
          >
            <MenuItem value=""><em>{t('common.none')}</em></MenuItem>
            <MenuItem value="M">M</MenuItem>
            <MenuItem value="F">F</MenuItem>
          </Select>
        </FormControl>
      );
    }

    if (column.column_name === 'age') {
      return (
        <FormControl fullWidth margin="normal" key={column.column_name}>
          <InputLabel>{t('cat.age')}</InputLabel>
          <Select
            value={formData[column.column_name] || ''}
            label={t('cat.age')}
            onChange={(e) => setFormData({ ...formData, [column.column_name]: e.target.value })}
          >
            <MenuItem value=""><em>{t('common.none')}</em></MenuItem>
            <MenuItem value="Kitten">{t('catAges.kitten')}</MenuItem>
            <MenuItem value="Adult">{t('catAges.adult')}</MenuItem>
            <MenuItem value="Senior">{t('catAges.senior')}</MenuItem>
          </Select>
        </FormControl>
      );
    }

    if (column.column_name === 'source') {
      return (
        <FormControl fullWidth margin="normal" key={column.column_name}>
          <InputLabel>{t('cat.source')}</InputLabel>
          <Select
            value={formData[column.column_name] || ''}
            label={t('cat.source')}
            onChange={(e) => setFormData({ ...formData, [column.column_name]: e.target.value })}
          >
            <MenuItem value=""><em>{t('common.none')}</em></MenuItem>
            <MenuItem value="ASL">{t('catSources.asl')}</MenuItem>
            <MenuItem value="Private">{t('catSources.private')}</MenuItem>
            <MenuItem value="Municipality">{t('catSources.municipality')}</MenuItem>
          </Select>
        </FormControl>
      );
    }

    if (column.column_name === 'municipality') {
      return (
        <FormControl fullWidth margin="normal" key={column.column_name}>
          <InputLabel>{t('cat.municipality')}</InputLabel>
          <Select
            value={formData[column.column_name] || ''}
            label={t('cat.municipality')}
            onChange={(e) => setFormData({ ...formData, [column.column_name]: e.target.value })}
          >
            <MenuItem value=""><em>{t('common.none')}</em></MenuItem>
            {municipalities.map((municipality: string) => (
              <MenuItem key={municipality} value={municipality}>
                {municipality}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    if (column.column_name === 'address_where_found') {
      return (
        <TextField
          key={column.column_name}
          fullWidth
          margin="normal"
          label={t('cat.addressWhereFound')}
          value={formData[column.column_name] || ''}
          onChange={(e) => setFormData({ ...formData, [column.column_name]: e.target.value })}
          multiline
          rows={2}
        />
      );
    }

    if (column.column_name === 'sterilized') {
      return null; // Sterilized is auto-updated by trigger
    }

    if (column.column_name === 'photo_file_path') {
      return (
        <Box key={column.column_name} sx={{ mt: 2, mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom fontWeight="bold">
            {t('cat.catPhoto')}
          </Typography>

          {/* Show existing photo */}
          {formData.photo_file_path && !editPhotoFile && (
            <Box sx={{ mb: 2 }}>
              <Box
                component="img"
                src={`${API_URL}${formData.photo_file_path}`}
                alt="Cat photo"
                sx={{
                  width: '100%',
                  maxWidth: 300,
                  height: 'auto',
                  borderRadius: 2,
                  border: '2px solid #a8edea',
                  mb: 1,
                }}
              />
              <Typography variant="caption" color="text.secondary" display="block">
                {t('cat.currentPhoto')}
              </Typography>
            </Box>
          )}

          {/* Show new photo preview */}
          {editPhotoFile && (
            <Box sx={{ mb: 2 }}>
              <Box
                component="img"
                src={URL.createObjectURL(editPhotoFile)}
                alt="New cat photo"
                sx={{
                  width: '100%',
                  maxWidth: 300,
                  height: 'auto',
                  borderRadius: 2,
                  border: '2px solid #4caf50',
                  mb: 1,
                }}
              />
              <Typography variant="caption" color="success.main" display="block" fontWeight="bold">
                {t('cat.newPhotoSelected')}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                {editPhotoFile.name} ({(editPhotoFile.size / 1024).toFixed(2)} KB)
              </Typography>
            </Box>
          )}

          {/* Upload button */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              component="label"
              size="small"
              sx={{
                background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
                color: '#333',
                textTransform: 'none',
                fontWeight: 600,
                flexGrow: 1,
              }}
            >
              {editPhotoFile ? t('cat.changePhoto') : formData.photo_file_path ? t('cat.replacePhoto') : t('cat.uploadPhoto')}
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={(e) => {
                  if (e.target.files && e.target.files[0]) {
                    setEditPhotoFile(e.target.files[0]);
                  }
                }}
              />
            </Button>
            {editPhotoFile && (
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={() => setEditPhotoFile(null)}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                {t('common.clear')}
              </Button>
            )}
          </Box>
        </Box>
      );
    }

    return (
      <TextField
        key={column.column_name}
        fullWidth
        margin="normal"
        label={column.column_name.replace(/_/g, ' ').toUpperCase()}
        type={column.data_type.includes('date') ? 'date' : column.data_type.includes('int') ? 'number' : 'text'}
        value={formData[column.column_name] || ''}
        onChange={(e) => setFormData({ ...formData, [column.column_name]: e.target.value })}
        InputLabelProps={column.data_type.includes('date') ? { shrink: true } : undefined}
      />
    );
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 2 }}>
      {message && (
        <Alert
          severity={message.type}
          sx={{
            mb: 2,
            borderRadius: 2,
            boxShadow: '0 4px 12px rgba(168,237,234,0.15)',
          }}
          onClose={() => setMessage(null)}
        >
          {message.text}
        </Alert>
      )}

      <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 700, color: '#4db6ac' }}>
            {t('lists.freedList')} ({filteredFreed.length})
          </Typography>
          <HelpButton
            title={t('help.freed.title')}
            intro={t('help.freed.intro')}
            items={t('help.freed.items', { returnObjects: true }) as string[]}
          />
        </Box>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/')}
          sx={{
            borderColor: '#4db6ac',
            color: '#4db6ac',
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': {
              borderColor: '#3d9a8f',
              background: 'rgba(168,237,234,0.05)',
            }
          }}
        >
          {t('common.backToMenu')}
        </Button>
      </Box>

      {/* Filter Section */}
      <Paper elevation={0} sx={{ mb: 1.5, borderRadius: 2, boxShadow: '0 2px 8px rgba(168,237,234,0.12)' }}>
        <Box
          sx={{ px: 1.5, py: 1, display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', userSelect: 'none' }}
          onClick={() => setFiltersExpanded(!filtersExpanded)}
        >
          <FilterIcon sx={{ fontSize: 18, color: '#38b2ac' }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1 }}>
            {t('lists.filterFreedCats')}
          </Typography>
          {filters.length > 0 && !filtersExpanded && (
            <Chip label={filters.length} size="small" sx={{ height: 20, bgcolor: '#38b2ac', color: 'white', fontSize: '0.7rem' }} />
          )}
          {filtersExpanded ? <ExpandLessIcon sx={{ color: 'text.secondary', fontSize: 20 }} /> : <ExpandMoreIcon sx={{ color: 'text.secondary', fontSize: 20 }} />}
        </Box>
        <Collapse in={filtersExpanded}>
          <Box sx={{ px: 1.5, pb: 1.5 }}>
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t('lists.field')}</InputLabel>
                  <Select value={currentFilterField} label={t('lists.field')} onChange={(e) => setCurrentFilterField(e.target.value)}>
                    <MenuItem value=""><em>{t('lists.selectFieldPlaceholder')}</em></MenuItem>
                    <MenuItem value="chip_id">{t('cat.chipId')}</MenuItem>
                    <MenuItem value="name">{t('cat.name')}</MenuItem>
                    <MenuItem value="sex">{t('cat.sex')}</MenuItem>
                    <MenuItem value="age">{t('cat.age')}</MenuItem>
                    <MenuItem value="breed">{t('cat.breed')}</MenuItem>
                    <MenuItem value="cost_class">{t('cat.costClass')}</MenuItem>
                    <MenuItem value="municipality">{t('cat.municipality')}</MenuItem>
                    <MenuItem value="source">{t('cat.source')}</MenuItem>
                    <MenuItem value="sterilized">{t('cat.sterilized')}</MenuItem>
                    <MenuItem value="fiv">FIV</MenuItem>
                    <MenuItem value="felv">FeLV</MenuItem>
                    <MenuItem value="area">{t('cat.area')}</MenuItem>
                    <MenuItem value="temporary_placement">{t('cat.temporaryPlacement')}</MenuItem>
                    <MenuItem value="freed_date">{t('checkout.freedDate')}</MenuItem>
                    <MenuItem value="referente_di_colonia">{t('cat.referenteDiColonia')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={5}>
                <TextField
                  fullWidth size="small" label={t('lists.value')} value={currentFilterValue}
                  onChange={(e) => setCurrentFilterValue(e.target.value)}
                  disabled={!currentFilterField} placeholder={t('lists.enterValue')}
                  onKeyPress={(e) => { if (e.key === 'Enter') addFilter(); }}
                />
              </Grid>
              <Grid item xs={6} md={1.5}>
                <Button fullWidth variant="contained" onClick={addFilter}
                  disabled={!currentFilterField || !currentFilterValue} startIcon={<AddIcon />}
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)', color: '#333' }}
                >
                  {t('common.add')}
                </Button>
              </Grid>
              <Grid item xs={6} md={1.5}>
                <Button fullWidth variant="outlined" onClick={clearAllFilters}
                  disabled={filters.length === 0 && !currentFilterField && !currentFilterValue} startIcon={<ClearIcon />}
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                >
                  {t('common.clear')}
                </Button>
              </Grid>
            </Grid>
            {filters.length > 0 && (
              <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', mr: 0.5 }}>{t('lists.active')}</Typography>
                {filters.map((filter) => (
                  <Chip
                    key={filter.id}
                    label={`${getFieldLabel(filter.field)}: ${filter.value}`}
                    onDelete={() => removeFilter(filter.id)}
                    size="small"
                    sx={{ background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)', color: '#333',
                      '& .MuiChip-deleteIcon': { color: 'rgba(0,0,0,0.4)', '&:hover': { color: '#333' } } }}
                  />
                ))}
              </Box>
            )}
          </Box>
        </Collapse>
      </Paper>

      {/* Master-Detail Layout */}
      <Box sx={{ display: 'flex', gap: 1.5, height: 'calc(100vh - 280px)', minHeight: 400 }}>
        {/* Master Table (Left Side) */}
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            flex: detailFreed ? '0 0 55%' : '1 1 100%',
            transition: 'flex 0.3s ease',
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(168,237,234,0.12)',
            overflow: 'auto',
          }}
        >
          <Table stickyHeader size="small" sx={{ tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: 36 }} />
              {(['id','chip_id','name','sex','color','municipality','fiv','felv','freed_date','referente_di_colonia','cost_class'] as const).map(col => (
                <col key={col} style={{ width: colWidths[col] }} />
              ))}
            </colgroup>
            <TableHead>
              <TableRow sx={{ '& th': { background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)', color: '#333', fontWeight: 700, padding: '6px 10px', fontSize: '0.75rem', position: 'relative', overflow: 'hidden', whiteSpace: 'nowrap' } }}>
                <TableCell sx={{ color: 'rgba(0,0,0,0.4)', fontWeight: 400 }}>#</TableCell>
                {([
                  { col: 'id', label: 'ID' },
                  { col: 'chip_id', label: t('cat.chipId') },
                  { col: 'name', label: t('cat.name') },
                  { col: 'sex', label: t('cat.sex') },
                  { col: 'color', label: t('cat.color') },
                  { col: 'municipality', label: t('cat.municipality') },
                  { col: 'fiv', label: t('cat.fiv') },
                  { col: 'felv', label: t('cat.felv') },
                  { col: 'freed_date', label: t('checkout.freedDate') },
                  { col: 'referente_di_colonia', label: t('cat.referenteDiColonia') },
                  { col: 'cost_class', label: t('cat.costClass') },
                ] as const).map(({ col, label }) => (
                  <TableCell key={col} onClick={() => handleSort(col)} sx={{ cursor: 'pointer', userSelect: 'none' }}>
                    <TableSortLabel
                      active={sortCol === col}
                      direction={sortCol === col ? sortDir : 'asc'}
                    >
                      {label}
                    </TableSortLabel>
                    <Box onMouseDown={(e) => handleResizeMouseDown(col, e)} onClick={(e) => e.stopPropagation()} sx={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 5, cursor: 'col-resize', zIndex: 1, '&:hover': { background: 'rgba(0,0,0,0.15)' } }} />
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={12} align="center"><Typography color="text.secondary">{t('common.loading')}</Typography></TableCell></TableRow>
              ) : filteredFreed.length === 0 ? (
                <TableRow><TableCell colSpan={12} align="center"><Typography color="text.secondary">{t('lists.noFreedCatsFound')}</Typography></TableCell></TableRow>
              ) : (
                sortedFreed.map((cat: Freed, index: number) => (
                  <TableRow
                    key={cat.id}
                    hover
                    onClick={() => handleRowClick(cat)}
                    sx={{
                      cursor: 'pointer',
                      backgroundColor: detailFreed?.id === cat.id ? 'rgba(168,237,234,0.15)' : 'inherit',
                      borderLeft: detailFreed?.id === cat.id ? '3px solid #4db6ac' : '3px solid transparent',
                      '& td': { padding: '4px 8px', fontSize: '0.8rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 0 },
                      '&:hover': { backgroundColor: detailFreed?.id === cat.id ? 'rgba(168,237,234,0.2)' : 'rgba(0,0,0,0.04)' },
                    }}
                  >
                    <TableCell sx={{ color: 'text.secondary', fontSize: '0.7rem', userSelect: 'none' }}>{index + 1}</TableCell>
                    <TableCell sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>{cat.id}</TableCell>
                    <TableCell sx={{ fontWeight: detailFreed?.id === cat.id ? 600 : 400 }}>{cat.chip_id || t('cat.missing')}</TableCell>
                    <TableCell sx={{ fontWeight: detailFreed?.id === cat.id ? 600 : 400 }}>{cat.name || 'N/A'}</TableCell>
                    <TableCell>{cat.sex || 'N/A'}</TableCell>
                    <TableCell>{cat.color || 'N/A'}</TableCell>
                    <TableCell>{cat.municipality || 'N/A'}</TableCell>
                    <TableCell><Chip label={cat.fiv === 'Y' ? 'Y' : cat.fiv === 'N' ? 'N' : '?'} size="small" color={cat.fiv === 'Y' ? 'error' : cat.fiv === 'N' ? 'success' : 'default'} sx={{ fontWeight: 600, height: 20, fontSize: '0.7rem', minWidth: 28 }} /></TableCell>
                    <TableCell><Chip label={cat.felv === 'Y' ? 'Y' : cat.felv === 'N' ? 'N' : '?'} size="small" color={cat.felv === 'Y' ? 'error' : cat.felv === 'N' ? 'success' : 'default'} sx={{ fontWeight: 600, height: 20, fontSize: '0.7rem', minWidth: 28 }} /></TableCell>
                    <TableCell>{formatDate(cat.freed_date)}</TableCell>
                    <TableCell>{cat.referente_di_colonia || 'N/A'}</TableCell>
                    <TableCell>{translateCostClass(t, cat.cost_class)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Detail Panel (Right Side) */}
        {detailFreed && (
          <Paper
            elevation={0}
            sx={{
              flex: '0 0 42%',
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(168,237,234,0.12)',
              overflow: 'auto',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Header with Photo */}
            <Box
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #a78bfa 100%)',
                p: 1.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
              }}
            >
              {detailFreed.photo_file_path ? (
                <Box
                  component="img"
                  src={`${API_URL}${detailFreed.photo_file_path}`}
                  alt={detailFreed.name || 'Freed cat photo'}
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 1.5,
                    objectFit: 'cover',
                    border: '2px solid white',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 1.5,
                    backgroundColor: 'rgba(255,255,255,0.25)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '2px solid white',
                  }}
                >
                  <PetsIcon sx={{ fontSize: 28, color: 'white' }} />
                </Box>
              )}
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem', color: 'white' }}>
                  {detailFreed.name || t('cat.unnamed')}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)' }}>
                  ID: {detailFreed.id} {`| Chip: ${detailFreed.chip_id || t('cat.missing')}`}
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={() => setDetailFreed(null)}
                sx={{ color: 'white' }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* Action Buttons */}
            <Box sx={{ p: 1, display: 'flex', gap: 0.5, borderBottom: '1px solid #eee' }}>
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={() => handleEditFreed(detailFreed)}
                size="small"
                sx={{
                  flex: 1,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 2,
                }}
              >
                {t('common.edit')}
              </Button>
              <Button
                variant="contained"
                startIcon={<MedicalIcon />}
                onClick={() => handleAddEventForFreed(detailFreed)}
                size="small"
                sx={{
                  flex: 1,
                  background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 2,
                  '&:hover': {
                    background: 'linear-gradient(135deg, #e082ea 0%, #e4465b 100%)',
                  },
                }}
              >
                {t('medical.medicalEvent')}
              </Button>
              <Button
                variant="contained"
                startIcon={<RestoreIcon />}
                onClick={() => handleRestoreClick(detailFreed)}
                size="small"
                sx={{
                  flex: 1,
                  background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
                  color: '#333',
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 2,
                }}
              >
                {t('data.restore')}
              </Button>
              <Button
                variant="outlined"
                startIcon={<VisibilityIcon />}
                onClick={() => navigate(`/cat-record/${detailFreed.id}`)}
                size="small"
                sx={{ flex: 1, textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
              >
                {t('external.openRecord')}
              </Button>
            </Box>

            {/* Detail Sections */}
            <Box sx={{ p: 1.5, flex: 1, overflow: 'auto' }}>
              {/* Basic Information */}
              <Typography variant="subtitle2" sx={{ color: '#667eea', fontWeight: 700, mb: 0.75, textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 0.5 }}>
                {t('cat.basicInfo')}
              </Typography>
              <Grid container spacing={1} sx={{ mb: 1 }}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', lineHeight: 1.2 }}>{t('cat.sex')}</Typography>
                  <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.8rem' }}>{detailFreed.sex || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', lineHeight: 1.2 }}>{t('cat.age')}</Typography>
                  <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.8rem' }}>{detailFreed.age || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', lineHeight: 1.2 }}>{t('cat.breed')}</Typography>
                  <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.8rem' }}>{detailFreed.breed || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', lineHeight: 1.2 }}>{t('cat.color')}</Typography>
                  <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.8rem' }}>{detailFreed.color || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', lineHeight: 1.2 }}>{t('cat.sterilized')}</Typography>
                  <Chip
                    label={detailFreed.sterilized === 'Y' ? t('common.yes') : detailFreed.sterilized === 'N' ? t('common.no') : t('common.unknown')}
                    size="small"
                    color={detailFreed.sterilized === 'Y' ? 'success' : 'default'}
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 1 }} />

              {/* Location & Source */}
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#4db6ac', mb: 0.75, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {t('cat.locationSource')}
              </Typography>
              <Grid container spacing={1} sx={{ mb: 1 }}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', lineHeight: 1.2 }}>{t('cat.municipality')}</Typography>
                  <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.8rem' }}>{detailFreed.municipality || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', lineHeight: 1.2 }}>{t('cat.source')}</Typography>
                  <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.8rem' }}>{detailFreed.source || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', lineHeight: 1.2 }}>{t('cat.addressWhereFound')}</Typography>
                  <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.8rem' }}>{detailFreed.address_where_found || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', lineHeight: 1.2 }}>{t('cat.temporaryPlacement')}</Typography>
                  <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.8rem' }}>{detailFreed.temporary_placement || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', lineHeight: 1.2 }}>{t('cat.area')}</Typography>
                  <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.8rem' }}>{detailFreed.area || 'N/A'}</Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 1 }} />

              {/* Health Status */}
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#4db6ac', mb: 0.75, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {t('cat.healthStatus')}
              </Typography>
              <Grid container spacing={1} sx={{ mb: 1 }}>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', lineHeight: 1.2 }}>{t('cat.initialHealthStatus')}</Typography>
                  <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.8rem' }}>{detailFreed.initial_health_status || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', lineHeight: 1.2 }}>{t('cat.fiv')}</Typography>
                  <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.8rem' }}>{detailFreed.fiv || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', lineHeight: 1.2 }}>{t('cat.felv')}</Typography>
                  <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.8rem' }}>{detailFreed.felv || 'N/A'}</Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 1 }} />

              {/* Dates */}
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#4db6ac', mb: 0.75, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {t('cat.dates')}
              </Typography>
              <Grid container spacing={1} sx={{ mb: 1 }}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', lineHeight: 1.2 }}>{t('cat.arrivalDate')}</Typography>
                  <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.8rem' }}>{formatDate(detailFreed.arrival_date)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', lineHeight: 1.2 }}>{t('cat.departureDate')}</Typography>
                  <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.8rem' }}>{formatDate(detailFreed.departure_date)}</Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 1 }} />

              {/* Freed Information */}
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#26a69a', mb: 0.75, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {t('checkout.freedInfo')}
              </Typography>
              <Grid container spacing={1} sx={{ mb: 1 }}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', lineHeight: 1.2 }}>{t('checkout.freedDate')}</Typography>
                  <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.8rem' }}>{formatDate(detailFreed.freed_date)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', lineHeight: 1.2 }}>{t('cat.referenteDiColonia')}</Typography>
                  <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.8rem' }}>{detailFreed.referente_di_colonia || 'N/A'}</Typography>
                </Grid>
              </Grid>

              {/* Cost Class History */}
              <Divider sx={{ my: 1 }} />
              <CostClassHistoryPanel
                entityId={detailFreed.id}
                entityTable="freed"
                onHistoryChanged={loadFreed}
              />

              {/* Notes */}
              {detailFreed.notes && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#4db6ac', mb: 0.75, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    {t('cat.notes')}
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontSize: '0.8rem' }}>
                    {detailFreed.notes}
                  </Typography>
                </>
              )}
            </Box>
          </Paper>
        )}
      </Box>

      {/* Edit Freed Dialog */}
      <Dialog
        open={editFreedOpen}
        onClose={() => {
          setEditFreedOpen(false);
          setEditPhotoFile(null);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(168,237,234,0.25)',
          }
        }}
      >
        <DialogTitle sx={{ pb: 1, background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)', color: '#333', fontWeight: 700 }}>
          {t('checkout.editFreedCat')}: {selectedFreed?.name || selectedFreed?.chip_id}
        </DialogTitle>
        <DialogContent sx={{ pt: '12px !important', pb: 1, px: 2 }}>
          {/* Identity Section */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#a8edea', mb: 0.75, fontSize: '0.8rem' }}>
            {t('cat.identity')}
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label={t('cat.chipId')} value={freedFormData.chip_id || ''} onChange={(e) => setFreedFormData({ ...freedFormData, chip_id: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label={t('cat.name')} value={freedFormData.name || ''} onChange={(e) => setFreedFormData({ ...freedFormData, name: e.target.value })} />
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('cat.sex')}</InputLabel>
                <Select value={freedFormData.sex || ''} label={t('cat.sex')} onChange={(e) => setFreedFormData({ ...freedFormData, sex: e.target.value })}>
                  <MenuItem value=""><em>{t('common.none')}</em></MenuItem>
                  <MenuItem value="M">M</MenuItem>
                  <MenuItem value="F">F</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={8}>
              {(() => {
                const ageValue = freedFormData.age || '';
                const parts = ageValue.split('/');
                const month = parts[0] || '';
                const year = parts[1] || '';
                return (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <FormControl sx={{ width: '45%' }} size="small">
                      <InputLabel>{t('cat.birthMonth')}</InputLabel>
                      <Select
                        value={month}
                        label={t('cat.birthMonth')}
                        onChange={(e) => setFreedFormData({ ...freedFormData, age: `${e.target.value}/${year}` })}
                      >
                        <MenuItem value=""><em>{t('common.month')}</em></MenuItem>
                        {['01','02','03','04','05','06','07','08','09','10','11','12'].map(m => (
                          <MenuItem key={m} value={m}>{m}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      sx={{ width: '55%' }}
                      size="small"
                      label={t('cat.birthYear')}
                      placeholder={t('common.year') || 'YYYY'}
                      value={year}
                      onChange={(e) => {
                        const newYear = e.target.value.replace(/\D/g, '').slice(0, 4);
                        setFreedFormData({ ...freedFormData, age: `${month}/${newYear}` });
                      }}
                      inputProps={{ maxLength: 4 }}
                    />
                  </Box>
                );
              })()}
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label={t('cat.breed')} value={freedFormData.breed || ''} onChange={(e) => setFreedFormData({ ...freedFormData, breed: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label={t('cat.color')} value={freedFormData.color || ''} onChange={(e) => setFreedFormData({ ...freedFormData, color: e.target.value })} />
            </Grid>
          </Grid>

          <Divider sx={{ my: 1 }} />

          {/* Location & Source Section */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#a8edea', mb: 0.75, fontSize: '0.8rem' }}>
            {t('cat.locationSource')}
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('cat.source')}</InputLabel>
                <Select value={freedFormData.source || ''} label={t('cat.source')} onChange={(e) => setFreedFormData({ ...freedFormData, source: e.target.value })}>
                  <MenuItem value=""><em>{t('common.none')}</em></MenuItem>
                  <MenuItem value="ASL">{t('cat.sources.asl')}</MenuItem>
                  <MenuItem value="Private">{t('cat.sources.private')}</MenuItem>
                  <MenuItem value="Municipality">{t('cat.sources.municipality')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('cat.municipality')}</InputLabel>
                <Select value={freedFormData.municipality || ''} label={t('cat.municipality')} onChange={(e) => setFreedFormData({ ...freedFormData, municipality: e.target.value })}>
                  <MenuItem value=""><em>{t('common.none')}</em></MenuItem>
                  {municipalities.map((m: string) => (
                    <MenuItem key={m} value={m}>{m}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label={t('cat.addressWhereFound')} value={freedFormData.address_where_found || ''} onChange={(e) => setFreedFormData({ ...freedFormData, address_where_found: e.target.value })} multiline rows={2} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label={t('cat.temporaryPlacement')} value={freedFormData.temporary_placement || ''} onChange={(e) => setFreedFormData({ ...freedFormData, temporary_placement: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label={t('cat.area')} value={freedFormData.area || ''} onChange={(e) => setFreedFormData({ ...freedFormData, area: e.target.value })} />
            </Grid>
          </Grid>

          <Divider sx={{ my: 1 }} />

          {/* Health Section */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#a8edea', mb: 0.75, fontSize: '0.8rem' }}>
            {t('cat.health')}
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label={t('cat.initialHealthStatus')} value={freedFormData.initial_health_status || ''} onChange={(e) => setFreedFormData({ ...freedFormData, initial_health_status: e.target.value })} />
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('cat.sterilized')}</InputLabel>
                <Select value={freedFormData.sterilized ?? 'U'} label={t('cat.sterilized')} onChange={(e) => setFreedFormData({ ...freedFormData, sterilized: e.target.value })}>
                  <MenuItem value="U">{t('common.unknown')}</MenuItem>
                  <MenuItem value="N">{t('common.no')}</MenuItem>
                  <MenuItem value="Y">{t('common.yes')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('cat.fiv')}</InputLabel>
                <Select value={freedFormData.fiv || 'Unknown'} label={t('cat.fiv')} onChange={(e) => setFreedFormData({ ...freedFormData, fiv: e.target.value })}>
                  <MenuItem value="Unknown">{t('common.unknown')}</MenuItem>
                  <MenuItem value="Y">{t('common.yes')}</MenuItem>
                  <MenuItem value="N">{t('common.no')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('cat.felv')}</InputLabel>
                <Select value={freedFormData.felv || 'Unknown'} label={t('cat.felv')} onChange={(e) => setFreedFormData({ ...freedFormData, felv: e.target.value })}>
                  <MenuItem value="Unknown">{t('common.unknown')}</MenuItem>
                  <MenuItem value="Y">{t('common.yes')}</MenuItem>
                  <MenuItem value="N">{t('common.no')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small" required>
                <InputLabel>{t('cat.costClass')}</InputLabel>
                <Select value={freedFormData.cost_class || 'Ordinary'} label={t('cat.costClass')} onChange={(e) => setFreedFormData({ ...freedFormData, cost_class: e.target.value })}>
                  <MenuItem value="Ordinary">{t('cat.costClasses.ordinary')}</MenuItem>
                  <MenuItem value="Increased">{t('cat.costClasses.increased')}</MenuItem>
                  <MenuItem value="Increased Kitten">{t('cat.costClasses.increasedKitten')}</MenuItem>
                  <MenuItem value="Increased Sickroom">{t('cat.costClasses.increasedSickroom')}</MenuItem>
                  <MenuItem value="Other">{t('cat.costClasses.other')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Divider sx={{ my: 1 }} />

          {/* Dates Section */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#a8edea', mb: 0.75, fontSize: '0.8rem' }}>
            {t('cat.dates')}
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <DateField label={t('cat.arrivalDate')} value={freedFormData.arrival_date || ''} onChange={(v) => setFreedFormData({ ...freedFormData, arrival_date: v })} />
            </Grid>
            <Grid item xs={6}>
              <DateField label={t('cat.departureDate')} value={freedFormData.departure_date || ''} onChange={(v) => setFreedFormData({ ...freedFormData, departure_date: v })} />
            </Grid>
            <Grid item xs={12}>
              <DateField label={t('checkout.freedDate')} value={freedFormData.freed_date || ''} onChange={(v) => setFreedFormData({ ...freedFormData, freed_date: v })} />
            </Grid>
          </Grid>

          <Divider sx={{ my: 1 }} />

          {/* Notes & Photo Section */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#a8edea', mb: 0.75, fontSize: '0.8rem' }}>
            {t('cat.notesPhoto')}
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label={t('cat.notes')} value={freedFormData.notes || ''} onChange={(e) => setFreedFormData({ ...freedFormData, notes: e.target.value })} multiline rows={2} />
            </Grid>
            <Grid item xs={12}>
              {/* Photo section */}
              {freedFormData.photo_file_path && !editPhotoFile && (
                <Box sx={{ mb: 1.5 }}>
                  <Box
                    component="img"
                    src={`${API_URL}${freedFormData.photo_file_path}`}
                    alt="Cat photo"
                    sx={{ width: '100%', maxWidth: 200, height: 'auto', borderRadius: 2, border: '2px solid #a8edea', mb: 0.5 }}
                  />
                  <Typography variant="caption" color="text.secondary" display="block">{t('cat.currentPhoto')}</Typography>
                </Box>
              )}
              {editPhotoFile && (
                <Box sx={{ mb: 1.5 }}>
                  <Box
                    component="img"
                    src={URL.createObjectURL(editPhotoFile)}
                    alt="New cat photo"
                    sx={{ width: '100%', maxWidth: 200, height: 'auto', borderRadius: 2, border: '2px solid #4caf50', mb: 0.5 }}
                  />
                  <Typography variant="caption" color="success.main" display="block" fontWeight="bold">{t('cat.newPhotoSelected')}</Typography>
                  <Typography variant="caption" color="text.secondary" display="block">{editPhotoFile.name} ({(editPhotoFile.size / 1024).toFixed(2)} KB)</Typography>
                </Box>
              )}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button variant="contained" component="label" size="small" sx={{ background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)', color: '#333', textTransform: 'none', fontWeight: 600, flexGrow: 1 }}>
                  {editPhotoFile ? t('cat.changePhoto') : freedFormData.photo_file_path ? t('cat.replacePhoto') : t('cat.uploadPhoto')}
                  <input type="file" hidden accept="image/*" onChange={(e) => { if (e.target.files && e.target.files[0]) setEditPhotoFile(e.target.files[0]); }} />
                </Button>
                {editPhotoFile && (
                  <Button variant="outlined" color="error" size="small" onClick={() => setEditPhotoFile(null)} sx={{ textTransform: 'none', fontWeight: 600 }}>
                    {t('common.clear')}
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setEditFreedOpen(false);
              setEditPhotoFile(null);
            }}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleUpdateFreed}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
              color: '#333',
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(168,237,234,0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #97dcd9 0%, #ed9fb6 100%)',
              }
            }}
          >
            {t('common.update')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Medical Event Dialog */}
      <Dialog
        open={addEventOpen}
        onClose={() => {
          setAddEventOpen(false);
          setSelectedFile(null);
          setEventFormData({ event_date: getTodayDate() });
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(168,237,234,0.25)',
          }
        }}
      >
        <DialogTitle sx={{ py: 1.5, fontSize: '1rem' }}>{t('medical.addMedicalEventFor')}: {eventFreed?.name || eventFreed?.chip_id}</DialogTitle>
        <DialogContent sx={{ pt: '12px !important', pb: 1, px: 2 }}>
          <FormControl fullWidth margin="normal">
            <InputLabel>{t('medical.eventType')} *</InputLabel>
            <Select
              value={eventFormData.event_type || ''}
              label={`${t('medical.eventType')} *`}
              onChange={(e) => setEventFormData({ ...eventFormData, event_type: e.target.value })}
              required
            >
              <MenuItem value="Vaccine">{t('medical.eventTypes.vaccine')}</MenuItem>
              <MenuItem value="Sterilization">{t('medical.eventTypes.sterilization')}</MenuItem>
              <MenuItem value="Treatment">{t('medical.eventTypes.treatment')}</MenuItem>
              <MenuItem value="Delivery">{t('medical.eventTypes.delivery')}</MenuItem>
              <MenuItem value="Surgery">{t('medical.eventTypes.surgery')}</MenuItem>
              <MenuItem value="Checkup">{t('medical.eventTypes.checkup')}</MenuItem>
              <MenuItem value="test_fiv_felv">{t('medical.eventTypes.testFivFelv')}</MenuItem>
              <MenuItem value="Travel Expenses">{t('medical.eventTypes.travelExpenses')}</MenuItem>
              <MenuItem value="Other">{t('medical.eventTypes.other')}</MenuItem>
            </Select>
          </FormControl>
          <DateField
            label={t('medical.eventDate')}
            value={eventFormData.event_date || ''}
            onChange={(v) => setEventFormData({ ...eventFormData, event_date: v })}
          />
          <TextField
            fullWidth
            margin="normal"
            label={t('medical.cost')}
            type="text"
            inputMode="decimal"
            value={eventFormData.cost || ''}
            onChange={(e) => {
              const v = e.target.value;
              if (v === '' || /^\d*\.?\d{0,2}$/.test(v)) setEventFormData({ ...eventFormData, cost: v });
            }}
          />
          <TextField
            fullWidth
            margin="normal"
            label={t('medical.veterinaryName')}
            value={eventFormData.veterinary_name || ''}
            onChange={(e) =>
              setEventFormData({ ...eventFormData, veterinary_name: e.target.value })
            }
          />
          <TextField
            fullWidth
            margin="normal"
            label={t('cat.notes')}
            multiline
            rows={3}
            value={eventFormData.notes || ''}
            onChange={(e) => setEventFormData({ ...eventFormData, notes: e.target.value })}
          />

          {/* Invoice Upload Section */}
          <Box sx={{ mt: 3, p: 2, border: '1px dashed #ccc', borderRadius: 2, bgcolor: '#f9f9f9' }}>
            <Typography variant="subtitle2" gutterBottom fontWeight="bold">
              {t('medical.invoiceUploadOptional')}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
              {t('medical.uploadDocument')}
            </Typography>

            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Button
                variant="contained"
                component="label"
                startIcon={<AttachFileIcon />}
                color="primary"
                sx={{ flexGrow: 1 }}
              >
                {t('common.browseFiles')}
                <input
                  type="file"
                  hidden
                  accept=".pdf,.png,.jpg,.jpeg"
                  onChange={handleFileChange}
                />
              </Button>
              {selectedFile && (
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={() => setSelectedFile(null)}
                >
                  {t('common.clear')}
                </Button>
              )}
            </Box>

            {selectedFile && (
              <Box sx={{ mt: 2, p: 1.5, bgcolor: 'white', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                <Typography variant="body2" fontWeight="medium" color="primary">
                  {t('common.fileSelected')}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  {selectedFile.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('common.size')}: {(selectedFile.size / 1024).toFixed(2)} KB
                </Typography>
              </Box>
            )}

            {!selectedFile && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', fontStyle: 'italic' }}>
                {t('common.noFileSelected')}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setAddEventOpen(false);
              setSelectedFile(null);
              setEventFormData({ event_date: getTodayDate() });
            }}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleAddEvent}
            variant="contained"
            disabled={!eventFormData.event_type}
            sx={{
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(240,147,251,0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #e082ea 0%, #e4465b 100%)',
              },
              '&:disabled': {
                background: '#ccc',
                color: '#666',
              }
            }}
          >
            {t('medical.addEvent')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Restore Confirmation Dialog */}
      <Dialog
        open={restoreDialogOpen}
        onClose={() => {
          setRestoreDialogOpen(false);
          setFreedToRestore(null);
        }}
      >
        <DialogTitle sx={{ py: 1.5, fontSize: '1rem' }}>{t('checkout.restoreToActive')}</DialogTitle>
        <DialogContent sx={{ pt: '12px !important', pb: 1, px: 2 }}>
          <Typography>
            {t('checkout.confirmRestore')} <strong>{freedToRestore?.name}</strong> {t('checkout.confirmRestoreBack')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, mb: 1.5 }}>
            {t('checkout.restoreDescription')}
          </Typography>
          <DateField
            label={t('checkout.reEntryDate')}
            value={reEntryDate}
            onChange={(v: string) => setReEntryDate(v)}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {t('checkout.reEntryDateHelp')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setRestoreDialogOpen(false);
              setFreedToRestore(null);
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleRestoreConfirm}
            variant="contained"
            color="primary"
          >
            {t('data.restore')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default FreedList;
